import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { ApiKeysPanel } from "components/ApiKeys";
import { BillingPane, HeaderCredits } from "components/Billing";
import type { GotoTarget } from "components/Dashboard";
import { DashboardPane } from "components/Dashboard";
import { CALLS_WINDOW_DAYS } from "components/Dashboard/CallsCard";
import { MyQubicsPane } from "components/MyQubics";
import { CabinetDrawer, NavGroups, useFoldedGroups } from "components/Nav";
import { BackIcon, BurgerIcon } from "components/Nav/nav.icons";
import type { PaneKey } from "components/Nav/nav.model";
import { DEFAULT_SUB, HOME_PANE, isRatesWorkflow, visibleNavGroups } from "components/Nav/nav.model";
import { ProfileCard } from "components/Profile";
import { RatesPane } from "components/Rates";
import { SettingsPane } from "components/Settings";
import { DEFAULT_DETAIL_TAB } from "components/ShipmentDetail/detail.model";
import { ShipmentsPane } from "components/Shipments";
import { StorePane } from "components/Store";
import { SIGN_IN_URL } from "constants/common.constants";
import { useActivity } from "hooks/useActivity";
import { useApiKeys } from "hooks/useApiKeys";
import { useCredits } from "hooks/useCredits";
import { useMediaQuery } from "hooks/useMediaQuery";
import { useProfile } from "hooks/useProfile";
import { useQubics } from "hooks/useQubics";
import { useShipments } from "hooks/useShipments";
import { useSubscriptions } from "hooks/useSubscriptions";
import { useUsageSeries } from "hooks/useUsageSeries";
import { hasLeaveGuard, leaveThrough } from "utils/leave.guard";
import type { ShipmentRoute } from "utils/route.utils";
import {
  isAliasPath,
  isCanonicalShipmentPath,
  isRetiredPath,
  pushRoute,
  pushShipmentRoute,
  readRoute,
  replaceRoute,
  replaceShipmentRoute,
} from "utils/route.utils";
import { clearStoredUser, getStoredUser } from "utils/user.utils";
import "styles/index.scss";

/** Below this the sidebar becomes an overlay behind the burger, as in the design. */
const DRAWER_QUERY = "(max-width: 1199px)";

/** How many rows the dashboard's activity feed shows. */
const ACTIVITY_LIMIT = 8;

export const App = () => {
  const [signedIn, setSignedIn] = useState(() => getStoredUser() !== null);
  const [pane, setPane] = useState<PaneKey>(() => readRoute().pane);
  const [subTab, setSubTab] = useState<Record<PaneKey, string | null>>(() => {
    const { pane: initialPane, sub } = readRoute();

    return sub === null ? DEFAULT_SUB : { ...DEFAULT_SUB, [initialPane]: sub };
  });
  /** The shipment whose page is open under My Shipments. */
  const [openShipment, setOpenShipment] = useState<ShipmentRoute | null>(() => readRoute().shipment);
  /** The same, for the popstate handler, which is registered once. */
  const openShipmentRef = useRef(openShipment);
  openShipmentRef.current = openShipment;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isDrawer = useMediaQuery(DRAWER_QUERY);
  const { folded, toggleFold } = useFoldedGroups();

  /**
   * Integrations was split into three panes, so its URLs open the dashboard.
   * The address bar is corrected in place: pushing would leave the dead path
   * one Back away, which is where the visitor just came from.
   */
  useEffect(() => {
    if (isRetiredPath()) replaceRoute(HOME_PANE, null);
    // `/cabinet/billing` (Stripe's return address) becomes the pane's own path; the query stays.
    if (isAliasPath()) replaceRoute(readRoute().pane, null);
  }, []);

  /**
   * A shipment URL naming a hidden or unknown tab opens on Cargo, so the
   * address bar is put right the same way — replaced, never pushed. Back and
   * Forward can land on such an entry too, hence the dependency.
   */
  useEffect(() => {
    if (openShipment !== null && !isCanonicalShipmentPath()) replaceShipmentRoute(openShipment);
  }, [openShipment]);

  // A drawer left open behind a widening window would never be reachable again.
  useEffect(() => {
    if (!isDrawer) setDrawerOpen(false);
  }, [isDrawer]);

  /**
   * Back and forward move through the menu the way they moved through it.
   *
   * By the time popstate fires the address bar has already moved, so leaving a
   * shipment with unsaved edits cannot be stopped — only undone: the shipment's
   * URL goes back on, the page asks, and the entry the visitor was headed for
   * is pushed once they choose to leave. A tab change stays on the shipment
   * and needs no asking.
   */
  useEffect(() => {
    const apply = () => {
      const { pane: nextPane, sub, shipment } = readRoute();

      setPane(nextPane);
      setSubTab((current) => ({ ...current, [nextPane]: sub ?? DEFAULT_SUB[nextPane] }));
      setOpenShipment(shipment);
    };

    const handlePopState = () => {
      const current = openShipmentRef.current;
      const leaving = current !== null && readRoute().shipment?.id !== current.id;

      if (!leaving || !hasLeaveGuard()) {
        apply();
        return;
      }

      const { pathname, search, hash } = window.location;
      const target = `${pathname}${search}${hash}`;

      pushShipmentRoute(current);
      leaveThrough(() => {
        window.history.pushState(null, "", target);
        apply();
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleSessionLost = useCallback(() => {
    clearStoredUser();
    setSignedIn(false);
  }, []);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const {
    profile,
    status: profileStatus,
    save,
    uploadImage,
    removeImage,
    changePassword,
    deleteAccount,
  } = useProfile(handleSessionLost);
  const isAdmin = profile?.is_admin === true;

  // Deep-link /cabinet/logistics-data must not stick for non-admins once we know who they are.
  useEffect(() => {
    if (profileStatus === "loading") return;
    if (pane !== "rates") return;
    if (isAdmin) return;

    setPane(HOME_PANE);
    pushRoute(HOME_PANE, null);
  }, [pane, profileStatus, isAdmin]);

  const {
    subscriptions,
    status: subscriptionsStatus,
    reload: reloadSubscriptions,
  } = useSubscriptions(handleSessionLost);
  const {
    shipments,
    status: shipmentsStatus,
    removeMany: removeShipments,
    add: addShipment,
    reload: reloadShipments,
  } = useShipments(handleSessionLost);
  const { qubics, status: qubicsStatus, reload: reloadQubics } = useQubics(handleSessionLost);
  const keys = useApiKeys(handleSessionLost);
  const { usage, status: usageStatus, reload: reloadUsage } = useUsageSeries(CALLS_WINDOW_DAYS, handleSessionLost);
  const { activity, status: activityStatus, reload: reloadActivity } = useActivity(ACTIVITY_LIMIT, handleSessionLost);
  const credits = useCredits(handleSessionLost);
  const reloadCredits = credits.reload;

  /**
   * The dashboard summarises everything the other panes do, so it goes stale as
   * soon as one of them changes something: following a shipment writes an
   * activity row and spends metered calls. It lives in the cabinet rather than
   * on its own page, so it has to ask again when it is opened — reloading in the
   * background, since the cards already have last-known numbers to show.
   */
  const refreshDashboard = useCallback(() => {
    reloadShipments();
    reloadSubscriptions();
    reloadUsage();
    reloadActivity();
    reloadCredits();
  }, [reloadShipments, reloadSubscriptions, reloadUsage, reloadActivity, reloadCredits]);

  /**
   * Ownership and usage move with every call the company makes, so the Store
   * and My Qubics both ask again when opened.
   */
  const refreshQubics = useCallback(() => {
    reloadQubics();
    reloadSubscriptions();
  }, [reloadQubics, reloadSubscriptions]);

  if (!signedIn || profileStatus === "unauthorized") {
    return (
      <section className="pc">
        <div className="pc-guard">
          <h1 className="pc-guard__h">Sign in to open your cabinet</h1>
          <p className="pc-guard__t">
            Your subscriptions, API keys and account settings are available once you are signed in to your Qubictron
            account.
          </p>
          <button type="button" className="pcbtn pcbtn--main" onClick={() => window.location.assign(SIGN_IN_URL)}>
            Sign in
          </button>
        </div>
      </section>
    );
  }

  /**
   * Jump to another pane, the way the dashboard's cards and shortcuts do. The
   * sub-tab travels with it so a card can point at a specific tab, not just a
   * section.
   */
  const goto = ({ pane: target, sub }: GotoTarget) =>
    leaveThrough(() => {
      setPane(target);
      setOpenShipment(null);

      if (sub !== undefined) {
        setSubTab((current) => ({ ...current, [target]: sub }));
      }

      pushRoute(target, sub ?? subTab[target]);
      setDrawerOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

  const openShipmentPage = (id: number) => {
    const next = { id, tab: DEFAULT_DETAIL_TAB };

    setOpenShipment(next);
    pushShipmentRoute(next);
    window.scrollTo(0, 0);
  };

  const selectShipmentTab = (tab: string) => {
    if (openShipment === null) return;

    const next = { ...openShipment, tab };

    setOpenShipment(next);
    pushShipmentRoute(next);
  };

  /** The breadcrumb and the round back button — asked first while there are unsaved edits. */
  const closeShipment = () =>
    leaveThrough(() => {
      setOpenShipment(null);
      pushRoute("shipments", "shipments");
    });

  const renderPane = (key: PaneKey): ReactNode => {
    switch (key) {
      case "dash":
        return (
          <DashboardPane
            profile={profile}
            shipments={shipments}
            shipmentsStatus={shipmentsStatus}
            subscriptions={subscriptions}
            subscriptionsStatus={subscriptionsStatus}
            usage={usage}
            usageStatus={usageStatus}
            activity={activity}
            activityStatus={activityStatus}
            credits={credits.state}
            onGoto={goto}
            onSessionLost={handleSessionLost}
            onOpened={refreshDashboard}
          />
        );
      case "shipments":
        return (
          <ShipmentsPane
            tab={subTab.shipments ?? "shipments"}
            openShipment={openShipment}
            shipments={shipments}
            status={shipmentsStatus}
            onRemove={removeShipments}
            onAdd={addShipment}
            onOpenShipment={openShipmentPage}
            onSelectShipmentTab={selectShipmentTab}
            onCloseShipment={closeShipment}
            onSessionLost={handleSessionLost}
            /* The tracking allowance is the gateway's API limit, shown under My Qubics. */
            onChoosePlan={() => goto({ pane: "myqubics" })}
            onBuyCredits={() => goto({ pane: "billing" })}
          />
        );
      case "billing":
        return <BillingPane credits={credits} onSessionLost={handleSessionLost} />;
      case "rates":
        if (!isAdmin) {
          return null;
        }

        return (
          <RatesPane
            tab={subTab.rates ?? "overview"}
            onSelectTab={(next) => {
              setSubTab((current) => ({ ...current, rates: next }));
              pushRoute("rates", next);
            }}
          />
        );
      case "store":
        return (
          <StorePane
            kind={subTab.store === "web" ? "web" : "api"}
            qubics={qubics}
            status={qubicsStatus}
            subscriptions={subscriptions}
            onSelectKind={(next) => {
              setSubTab((current) => ({ ...current, store: next }));
              pushRoute("store", next);
            }}
            onManage={() => goto({ pane: "myqubics" })}
            onOpened={refreshQubics}
          />
        );
      case "myqubics":
        return (
          <MyQubicsPane
            subscriptions={subscriptions}
            status={subscriptionsStatus}
            qubics={qubics}
            onOpenStore={(kind) => goto({ pane: "store", sub: kind })}
            onOpened={refreshQubics}
          />
        );
      case "apikeys":
        return (
          <div className="pc-pane">
            <ApiKeysPanel keys={keys} qubics={qubics} subscriptions={subscriptions} />
          </div>
        );
      case "settings":
        return profile === null ? (
          <div className="pc-pane">
            <div className="pcx">
              {profileStatus === "loading" ? (
                <span className="pc-spin" aria-label="Loading" />
              ) : (
                <p className="pc-note pc-note--bad">Could not load your account. Please refresh the page.</p>
              )}
            </div>
          </div>
        ) : (
          <SettingsPane profile={profile} onSave={save} onChangePassword={changePassword} onDelete={deleteAccount} />
        );
    }
  };

  const selectPane = (key: PaneKey) => {
    leaveThrough(() => {
      setPane(key);
      setOpenShipment(null);
      pushRoute(key, subTab[key]);
      setDrawerOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const selectSub = (key: PaneKey, sub: string) => {
    leaveThrough(() => {
      setSubTab((current) => ({ ...current, [key]: sub }));
      setOpenShipment(null);
      pushRoute(key, sub);
      setDrawerOpen(false);
    });
  };

  const ratesWorkflow = isAdmin && isRatesWorkflow(pane, subTab.rates);

  const profileCard = (
    <ProfileCard profile={profile} onSave={save} onUploadImage={uploadImage} onRemoveImage={removeImage} />
  );

  const navGroups = (
    <NavGroups
      groups={visibleNavGroups(isAdmin)}
      pane={pane}
      subTab={subTab}
      folded={folded}
      onToggleFold={toggleFold}
      onSelectPane={selectPane}
      onSelectSub={selectSub}
    />
  );

  /** A shipment's page is full-bleed in the design: the menu gives way to #pc-back. */
  const shipmentPage = pane === "shipments" && openShipment !== null;

  return (
    <section className={cn("pc", { "pc--rates-workflow": ratesWorkflow, "pc--shipment": shipmentPage })}>
      {/* Portalled into the site header, next to the account button. */}
      <HeaderCredits
        credits={credits}
        onOpenBilling={() => goto({ pane: "billing" })}
        onSessionLost={handleSessionLost}
      />
      <div className="pc__grid">
        {/*
         * The design puts a round #pc-back beside a full-bleed page. RatesPane
         * already draws its own, so its workflow gets none here; a shipment's
         * page gets this one.
         */}
        {shipmentPage && (
          <button type="button" className="pc-back" id="pc-back" aria-label="Back" onClick={closeShipment}>
            <BackIcon />
          </button>
        )}

        {!ratesWorkflow && !shipmentPage && (
          <>
            {!isDrawer && profileCard}

            {isDrawer ? (
              <>
                <button
                  type="button"
                  className="pc-back pc-burger"
                  id="pc-menu"
                  aria-label="Open menu"
                  aria-expanded={drawerOpen}
                  onClick={() => setDrawerOpen((open) => !open)}
                >
                  <BurgerIcon />
                </button>

                <CabinetDrawer open={drawerOpen} onClose={closeDrawer}>
                  {profileCard}
                  {navGroups}
                </CabinetDrawer>
              </>
            ) : (
              navGroups
            )}
          </>
        )}

        <div>{renderPane(pane)}</div>
      </div>
    </section>
  );
};
